import { AssessmentRecipientRow } from '../features/surveys/assessmentRecipientsApi';
import { assessmentSendSingleStateKey } from '../features/surveys/assessmentRecipientsSlice';

interface RecipientListTableProps {
  recipients: AssessmentRecipientRow[];
  /** Encuesta actual; necesario para no mezclar estado de envío entre encuestas con el mismo destinatario. */
  assessmentId?: string;
  onRemove: (recipientId: string) => void;
  onClear: () => void;
  onSend?: (recipientId: string) => void;
  sendingSingle?: Record<string, boolean>;
  sendSingleResult?: Record<string, { success: boolean; message?: string }>;
}

export function RecipientListTable({
  recipients,
  assessmentId,
  onRemove,
  onClear,
  onSend,
  sendingSingle = {},
  sendSingleResult = {},
}: RecipientListTableProps) {
  if (recipients.length === 0) {
    return (
      <div className="text-center py-8 border border-dashed rounded-md">
        <p className="text-muted-foreground text-sm">No hay destinatarios agregados</p>
        <p className="text-xs text-muted-foreground mt-1">
          Usa el campo de búsqueda arriba para agregar destinatarios
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-foreground">
          {recipients.length} destinatario{recipients.length !== 1 ? 's' : ''}
        </span>
        {recipients.length > 0 && (
          <button
            onClick={onClear}
            className="text-xs text-destructive hover:underline"
          >
            Limpiar lista
          </button>
        )}
      </div>

      <div className="border rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr key="header" className="bg-muted border-b">
              <th className="text-left px-3 py-2 font-medium text-foreground">Nombre</th>
              <th className="text-left px-3 py-2 font-medium text-foreground">Email</th>
              <th className="text-left px-3 py-2 font-medium text-foreground">Rol</th>
              <th className="text-left px-3 py-2 font-medium text-foreground">Estado</th>
              <th className="text-right px-3 py-2 font-medium text-foreground">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {recipients.filter(Boolean).map((recipient, index) => {
              const recipientId = recipient?.id || recipient?.externalRecipientId || `idx-${index}`;
              const sendStateKey =
                assessmentId && recipientId
                  ? assessmentSendSingleStateKey(assessmentId, recipientId)
                  : recipientId;
              const isSending = sendingSingle[sendStateKey];
              const sendResult = sendSingleResult[sendStateKey];
              const isSent = recipient?.tokenSentAt || sendResult?.success;

              return (
                <tr key={recipientId} className="border-b last:border-b-0">
                  <td className="px-3 py-2">
                    {recipient?.externalRecipient?.firstName} {recipient?.externalRecipient?.lastName}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {recipient?.externalRecipient?.email}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-xs bg-muted px-1.5 py-0.5 rounded">
                      {recipient?.externalRecipient?.role}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {isSending ? (
                      <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                        <span className="inline-block w-3 h-3 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                        Enviando...
                      </span>
                    ) : isSent ? (
                      <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                        {sendResult?.message || 'Enviado'}
                      </span>
                    ) : sendResult && !sendResult.success ? (
                      <span className="text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded" title={sendResult.message}>
                        Error
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Pendiente</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      {onSend && (
                        <button
                          onClick={() => onSend(recipientId)}
                          disabled={isSending}
                          className="text-xs text-primary hover:underline disabled:opacity-50"
                        >
                          {isSending ? 'Enviando...' : isSent ? 'Reenviar' : 'Enviar'}
                        </button>
                      )}
                      <button
                        onClick={() => recipient?.id && onRemove(recipient.id)}
                        className="text-xs text-destructive hover:underline"
                      >
                        Quitar
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
